/* Stories Player — native replacement for the Tolstoy "Shop the feed" PDP widget.
 *
 * Closed state: a horizontal rail of poster tiles (rendered in Liquid).
 * Open state: a modal (desktop, bottom-right) / fullscreen (mobile) player.
 *
 * Behaviour kept (per product decision): progress bars (one per story),
 * swipe / click-zone / arrow-key navigation, mute/unmute, close (X / ESC /
 * click outside), autoplay-next on video end.
 * Dropped vs Tolstoy: share, triple-dot menu, shopping bag, recommended
 * products, comment overlay.
 *
 * Each <stories-player> reads its stories from the JSON in its
 * <script type="application/json"> child, so multiple instances on a page
 * never collide. */

class StoriesPlayer extends HTMLElement {
  constructor() {
    super();
    this._bound = false;
    this._index = 0;
    this._playing = false;
    this._muted = false; // sound ON by default; opening a story is a user gesture
    this._rafId = null;
  }

  connectedCallback() {
    if (this._bound) return;
    this._bound = true;

    // Read stories data
    const dataEl = this.querySelector('script[type="application/json"]');
    this._stories = [];
    if (dataEl) {
      try {
        this._stories = JSON.parse(dataEl.textContent || '[]');
      } catch (e) {
        this._stories = [];
      }
    }
    // Filter to stories with a usable video
    this._stories = this._stories.filter((s) => s && (s.video || s.poster));
    if (!this._stories.length) {
      // No content → render nothing (no broken UI)
      this.remove();
      return;
    }

    // Closed-state tile buttons
    this._tiles = [...this.querySelectorAll('[data-sp-tile]')];
    this._tiles.forEach((tile, i) => {
      tile.addEventListener('click', () => this.open(i));
    });

    // Build the open-state viewer once (kept in DOM, hidden until open)
    this._buildViewer();

    // Keyboard + outside-click handled on open
    this._onKeydown = this._onKeydown.bind(this);

    // Rail auto-preview — tile videos play one at a time (8–10s each).
    this._modalOpen = false;
    this._railInterval = 9000;
    this._railIndex = 0;
    this._railTimer = null;
    this._railActive = false;
    this._tileVideos = [...this.querySelectorAll('[data-sp-tile-video]')];
    this._setupRailAutoplay();
  }

  disconnectedCallback() {
    this._stopRailAutoplay();
    if (this._railObserver) {
      try { this._railObserver.disconnect(); } catch (e) {}
    }
  }

  /* ---------- Rail auto-preview: play tile videos one at a time ---------- */
  _setupRailAutoplay() {
    if (!this._tileVideos || !this._tileVideos.length) return;
    // Respect reduced motion — leave tiles resting on their first frame.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this._tileVideos.forEach((v) => {
      v.muted = true;
      v.setAttribute('muted', '');
      v.playsInline = true;
    });
    // Start/stop with visibility (perf + honours muted-autoplay policy).
    const railEl = this.querySelector('.sp__rail') || this;
    if ('IntersectionObserver' in window) {
      this._railObserver = new IntersectionObserver((entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible) this._startRailAutoplay();
        else this._stopRailAutoplay();
      }, { threshold: 0.2 });
      this._railObserver.observe(railEl);
    } else {
      this._startRailAutoplay();
    }
    // Pause the rail when the tab is hidden; resume when visible again.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this._stopRailAutoplay();
      else if (!this._modalOpen) this._startRailAutoplay();
    });
  }

  _startRailAutoplay() {
    if (!this._tileVideos || !this._tileVideos.length) return;
    if (this._modalOpen || this._railActive) return;
    this._railActive = true;
    this._playRailTile(this._railIndex || 0);
  }

  _playRailTile(index) {
    if (!this._railActive) return;
    const n = this._tileVideos.length;
    this._railIndex = ((index % n) + n) % n;
    const current = this._tileVideos[this._railIndex];
    // Pause all others, play the current one from the start.
    this._tileVideos.forEach((v, i) => {
      if (i !== this._railIndex) {
        try { v.pause(); } catch (e) {}
      }
    });
    if (!current) return;
    try {
      current.muted = true;
      current.currentTime = 0;
      const p = current.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {}
    // Advance to the next tile after the preview window.
    clearTimeout(this._railTimer);
    this._railTimer = setTimeout(() => {
      try { current.pause(); } catch (e) {}
      this._playRailTile(this._railIndex + 1);
    }, this._railInterval);
  }

  _stopRailAutoplay() {
    this._railActive = false;
    clearTimeout(this._railTimer);
    if (this._tileVideos) {
      this._tileVideos.forEach((v) => {
        try { v.pause(); } catch (e) {}
      });
    }
  }

  /* ---------- Build the player overlay ---------- */
  _buildViewer() {
    const overlay = document.createElement('div');
    overlay.className = 'sp__overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = `
      <div class="sp__viewer" role="dialog" aria-modal="true" aria-label="Video stories">
        <div class="sp__progress"><span class="sp__progress-fill" data-sp-progress></span></div>
        <video class="sp__video" data-sp-video playsinline webkit-playsinline preload="none"></video>
        <button type="button" class="sp__tap" data-sp-tap aria-label="Play or pause video" tabindex="-1"></button>
        <button type="button" class="sp__play" data-sp-play aria-label="Pause video" tabindex="-1">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </button>
        <p class="sp__caption" data-sp-caption></p>
        <button type="button" class="sp__arrow sp__arrow--prev" data-sp-prev aria-label="Previous story">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M15.5 4.5L8 12l7.5 7.5 1.4-1.4L10.8 12l6.1-6.1z"/>
          </svg>
        </button>
        <button type="button" class="sp__arrow sp__arrow--next" data-sp-next aria-label="Next story">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M8.5 4.5L7.1 5.9 13.2 12l-6.1 6.1L8.5 19.5 16 12z"/>
          </svg>
        </button>
        <button type="button" class="sp__close" data-sp-close aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false">
            <path d="M6 6l12 12M18 6L6 18"/>
          </svg>
        </button>
        <button type="button" class="sp__mute" data-sp-mute aria-pressed="false" aria-label="Unmute">
          <svg class="sp__mute-off" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M4 9v6h4l5 5V4L8 9H4zm12.5 3a4.5 4.5 0 0 0-2.5-4.03v8.06A4.5 4.5 0 0 0 16.5 12zM3 3l18 18-1.4 1.4-2.7-2.7A8.9 8.9 0 0 1 14 21v-2a6.9 6.9 0 0 0 2-.6L1.6 4.4 3 3z"/>
          </svg>
          <svg class="sp__mute-on" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
            <path d="M4 9v6h4l5 5V4L8 9H4zm12.5 3a4.5 4.5 0 0 0-2.5-4.03v8.06A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06a9 9 0 0 0 0-17.54z"/>
          </svg>
        </button>
        <p class="sp__sr" data-sp-sr role="status" aria-live="polite"></p>
      </div>
    `;
    this._overlay = overlay;
    this._viewer = overlay.querySelector('[data-sp-close]').closest('.sp__viewer');
    this._video = overlay.querySelector('[data-sp-video]');
    this._caption = overlay.querySelector('[data-sp-caption]');
    this._progress = overlay.querySelector('[data-sp-progress]');
    this._closeBtn = overlay.querySelector('[data-sp-close]');
    this._muteBtn = overlay.querySelector('[data-sp-mute]');
    this._prevBtn = overlay.querySelector('[data-sp-prev]');
    this._nextBtn = overlay.querySelector('[data-sp-next]');
    this._tap = overlay.querySelector('[data-sp-tap]');
    this._playBtn = overlay.querySelector('[data-sp-play]');
    this._sr = overlay.querySelector('[data-sp-sr]');

    this.appendChild(overlay);
    // NOTE: position:fixed is broken when an ancestor has a transform/filter
    // (e.g. .product__info-wrapper.scroll-trigger). To anchor the modal to the
    // viewport (right:0 / bottom:0 of the PAGE, not the product info div), we
    // PORTAL the overlay to <body> on open and move it back on close.

    // Wire controls
    this._closeBtn.addEventListener('click', () => this.close());
    this._muteBtn.addEventListener('click', () => this._toggleMute());
    this._prevBtn.addEventListener('click', () => this._go(-1));
    this._nextBtn.addEventListener('click', () => this._go(1));
    // Tap on the video area (or the play button) toggles play/pause.
    // Ignore the synthetic click that follows a touch swipe (handled above).
    this._tap.addEventListener('click', () => {
      if (this._swiped) return;
      this._togglePlay();
    });
    this._playBtn.addEventListener('click', () => this._togglePlay());
    // Click outside the viewer (on overlay) closes — desktop only has transparent overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });

    // Video events
    this._video.addEventListener('loadedmetadata', () => this._startProgress());
    this._video.addEventListener('timeupdate', () => this._tickProgress());
    this._video.addEventListener('ended', () => this._go(1));
    this._video.addEventListener('error', () => this._go(1));
    // Play/pause state → toggle the play-button affordance + aria
    this._video.addEventListener('play', () => this._onPlayState(true));
    this._video.addEventListener('pause', () => this._onPlayState(false));

    // Touch swipe (mobile)
    this._wireSwipe();

    // Pause when tab hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this._playing) this._video.pause();
      else if (!document.hidden && this._playing) this._video.play().catch(() => {});
    });
  }

  /* ---------- Open / close ---------- */
  open(index) {
    this._modalOpen = true;
    this._stopRailAutoplay();
    this._index = Math.max(0, Math.min(index, this._stories.length - 1));
    // Portal overlay to <body> so position:fixed anchors to the viewport
    // (not to a transformed ancestor like .product__info-wrapper).
    if (this._overlay.parentNode !== document.body) {
      document.body.appendChild(this._overlay);
    }
    this._overlay.classList.add('is-open');
    this._overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sp-lock');
    document.addEventListener('keydown', this._onKeydown);
    // Focus the close button for keyboard users
    requestAnimationFrame(() => this._closeBtn.focus());
    this._load(this._index, true);
  }

  close() {
    this._overlay.classList.remove('is-open');
    this._overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sp-lock');
    document.removeEventListener('keydown', this._onKeydown);
    this._stopProgress();
    this._video.pause();
    this._video.removeAttribute('src');
    this._video.load();
    this._playing = false;
    // Move the overlay back into the component so it doesn't linger on <body>
    if (this._overlay.parentNode === document.body) {
      this.appendChild(this._overlay);
    }
    // Return focus to the tile that opened it
    const tile = this._tiles[this._index];
    if (tile) tile.focus();
    // Resume the rail auto-preview
    this._modalOpen = false;
    this._startRailAutoplay();
  }

  _onKeydown(e) {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this._go(-1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        this._go(1);
        break;
      case 'm':
      case 'M':
        this._toggleMute();
        break;
    }
  }

  /* ---------- Navigation ---------- */
  _go(dir) {
    const next = this._index + dir;
    if (next < 0) {
      // Stay on first
      this._video.currentTime = 0;
      return;
    }
    if (next >= this._stories.length) {
      // After last → close
      this.close();
      return;
    }
    this._load(next, true);
  }

  _load(index, autoplay) {
    this._index = index;
    const s = this._stories[index];
    if (!s) return;

    // Reset the single progress bar to 0 (fills as the new video plays)
    if (this._progress) this._progress.style.width = '0%';

    // Hide the prev arrow on the first story (nowhere to go back to)
    if (this._viewer) {
      if (index === 0) this._viewer.setAttribute('data-at-first', '');
      else this._viewer.removeAttribute('data-at-first');
    }

    // Caption
    this._caption.textContent = s.caption || '';
    this._sr.textContent = s.caption ? `Story ${index + 1} of ${this._stories.length}: ${s.caption}` : `Story ${index + 1} of ${this._stories.length}`;

    // Poster + video
    this._stopProgress();
    this._video.pause();
    if (s.poster) this._video.poster = s.poster;
    else this._video.removeAttribute('poster');
    if (s.video) {
      this._video.src = s.video;
      this._video.load();
      if (autoplay) {
        // Start with sound ON by default (opening/advancing a story via a click,
        // swipe, or arrow is a user gesture, so unmuted playback is permitted).
        // Honor the user's mute choice if they've toggled it this session.
        this._video.muted = this._muted;
        this._reflectMute();
        // Default to the paused affordance until the play promise resolves;
        // the play/pause listeners will flip it to the correct state.
        this._onPlayState(false);
        this._video.play().then(() => {
          this._playing = true;
        }).catch(() => {
          // Unmuted autoplay was blocked (no user activation, e.g. auto-advance
          // after a video ends). Retry muted so the story still plays; keep the
          // sound preference so the next gesture-driven load tries sound again.
          if (!this._video.muted) {
            this._video.muted = true;
            this._reflectMute();
            this._video.play().then(() => {
              this._playing = true;
            }).catch(() => {
              this._playing = false;
              this._onPlayState(false);
            });
          } else {
            this._playing = false;
            this._onPlayState(false);
          }
        });
      }
    } else {
      // No video — skip forward after a brief pause on the poster
      this._video.removeAttribute('src');
      setTimeout(() => this._go(1), 2500);
    }
  }

  /* ---------- Single progress bar ---------- */
  _startProgress() {
    this._stopProgress();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      if (this._progress) this._progress.style.width = '100%';
      return;
    }
    this._tickProgress();
  }

  _tickProgress() {
    const v = this._video;
    if (!v.duration) return;
    const pct = v.duration ? (v.currentTime / v.duration) * 100 : 0;
    if (this._progress) this._progress.style.width = pct + '%';
  }

  _stopProgress() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
  }

  /* ---------- Mute ---------- */
  /* Reflect the current video.muted state on the mute button (icon + a11y). */
  _reflectMute() {
    const muted = this._video.muted;
    this._muteBtn.setAttribute('aria-pressed', String(!muted));
    this._muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }

  _toggleMute() {
    // Remember the user's explicit choice so it persists across story navigation.
    this._muted = !this._video.muted;
    this._video.muted = this._muted;
    this._reflectMute();
    // If autoplay had failed, an unmute attempt counts as interaction → play
    if (!this._muted && !this._playing) {
      this._video.play().then(() => {
        this._playing = true;
      }).catch(() => {});
    }
  }

  /* ---------- Play / pause toggle (Tolstoy-style tap) ---------- */
  _togglePlay() {
    const v = this._video;
    if (!v || !v.src) return;
    if (v.paused) {
      v.play().then(() => { this._playing = true; }).catch(() => {});
    } else {
      v.pause();
      this._playing = false;
    }
  }

  _onPlayState(isPlaying) {
    this._playing = isPlaying;
    if (isPlaying) {
      this._viewer.classList.remove('sp--paused');
      this._viewer.classList.add('sp--playing');
      this._playBtn.setAttribute('aria-label', 'Pause video');
      this._tap.setAttribute('aria-label', 'Pause video');
    } else {
      this._viewer.classList.add('sp--paused');
      this._viewer.classList.remove('sp--playing');
      this._playBtn.setAttribute('aria-label', 'Play video');
      this._tap.setAttribute('aria-label', 'Play video');
    }
  }

  /* ---------- Touch swipe ---------- */
  _wireSwipe() {
    let startX = 0;
    let startY = 0;
    let active = false;
    // Tracks whether the current touch became a swipe; the tap-to-toggle
    // handler reads this so a swipe navigates WITHOUT also pausing the video.
    this._swiped = false;
    this._viewer.addEventListener('touchstart', (e) => {
      const t = e.changedTouches[0];
      startX = t.clientX;
      startY = t.clientY;
      active = true;
      this._swiped = false;
    }, { passive: true });
    this._viewer.addEventListener('touchmove', (e) => {
      if (!active) return;
      const t = e.changedTouches[0];
      if (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10) {
        this._swiped = true;
      }
    }, { passive: true });
    this._viewer.addEventListener('touchend', (e) => {
      if (!active) return;
      active = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Horizontal swipe → navigate; ignore if more vertical (scroll)
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        this._go(dx < 0 ? 1 : -1);
      }
    }, { passive: true });
  }
}

customElements.define('stories-player', StoriesPlayer);
